// Plan management (SM-844 / SM-845 / SM-846): the root broker's OWNER and
// FINANCE author the tree's fee plans — create a plan, publish versions per
// currency (now or scheduled), retire a plan — and assign them: a GROUP,
// BRANCH or NODE rule, or a clear, now or later; and the same for its
// revenue-share plans (the split of the broker commission). They go
// through the same broker-service methods the exchange uses. Every write is
// a confirm with a reason and the unknown-outcome lock (components/fees/*).
//
// FEE_MANAGE is listed only for a root OWNER or FINANCE membership and is
// never inherited, so this page is ANCHORED to the root membership node:
// with a descendant selected it offers to switch to the root instead of a
// refusal. Every other role meets the guard, which shows the server's own
// 403 from the probe; the server stays the boundary for a crafted request.
//
// ?tab= picks the panel (absent = fee plans); ?plan= is the open plan.
//
//   GET  /api/broker/{root}/fee-plans?offset=&limit=    FeePlanListResponse   (also the guard's probe)
//   GET  /api/broker/{root}/fee-plans/{plan}            FeePlanItem
//   POST /api/broker/{root}/fee-plans                   CreateFeePlanRequest → FeePlanItem
//   POST /api/broker/{root}/fee-plans/{plan}/versions   PublishFeePlanVersionRequest → FeePlanVersionItem
//   POST /api/broker/{root}/fee-plans/{plan}/retire     RetireFeePlanRequest → FeePlanItem
//   GET  /api/broker/{root}/fee-assignments             FeeAssignmentListResponse
//   POST /api/broker/{root}/fee-assignments             PublishFeeAssignmentRequest → FeeAssignmentItem
//   GET  /api/broker/{root}/revenue-share-plans[/{plan}], POST …, …/{plan}/versions, …/{plan}/retire
//   GET  /api/broker/{root}/revenue-share-assignments, POST …   (the operator bodies, broker_revenue.go)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { useSelectNode } from "@/components/layout/NodeSelector";
import { Button } from "@/components/ui/Button";
import { TabPanel, Tabs } from "@/components/ui/Tabs";
import { FeePlansPanel, FeeRulesCard, PLAN_PAGE_LIMIT } from "@/components/fees/FeePlansPanel";
import { FeeAssignmentsPanel } from "@/components/fees/FeeAssignmentsPanel";
import { SharePlansPanel, ShareRulesCard } from "@/components/fees/SharePlansPanel";
import { ShareAssignmentsPanel } from "@/components/fees/ShareAssignmentsPanel";
import { feeManageRoot, treeRootOf } from "@/lib/capabilities";
import { fetchFeePlans, keys } from "@/lib/queries";
import type { StaffScopeItem } from "@/lib/types";
import type { TranslationKey } from "@/i18n/en";
import { useI18n } from "@/i18n/I18nProvider";

/** The panels, in strip order. */
export const PLAN_TABS = ["fee-plans", "fee-assignments", "share-plans", "share-assignments"] as const;
export type PlanTab = (typeof PLAN_TABS)[number];
const DEFAULT_TAB: PlanTab = "fee-plans";

const TAB_LABEL: Record<PlanTab, TranslationKey> = {
  "fee-plans": "feePlans.tab.feePlans",
  "fee-assignments": "feePlans.tab.feeAssignments",
  "share-plans": "feePlans.tab.sharePlans",
  "share-assignments": "feePlans.tab.shareAssignments",
};

function isPlanTab(v: unknown): v is PlanTab {
  return typeof v === "string" && (PLAN_TABS as readonly string[]).includes(v);
}

interface PlansSearch {
  tab?: PlanTab;
  /** The open plan's id (on a plans tab). */
  plan?: string;
}

export const Route = createFileRoute("/_app/fees/plans")({
  validateSearch: (s: Record<string, unknown>): PlansSearch => ({
    tab: isPlanTab(s.tab) ? s.tab : undefined,
    plan: typeof s.plan === "string" && s.plan ? s.plan : undefined,
  }),
  beforeLoad: capabilityGuard("FEE_MANAGE"),
  component: PlanManagementPage,
});

function PlanManagementPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scope = useScope();
  const idBase = useId();
  const treeRoot = treeRootOf(scope.staff, scope.nodeId, scope.path);
  const manage = feeManageRoot(scope.staff, treeRoot);

  // The caller authors this tree's plans on its root, not on the node selected.
  if (guard.kind !== "allow" && manage && manage.broker.broker_id !== scope.nodeId) {
    return <SwitchToRoot root={manage} />;
  }

  // Allowed: the selected node IS the root membership node (FEE_MANAGE is
  // its own). Refused: the probe asks for the tree root's plans when the
  // caller covers the root, else for the selected node's — either way the
  // words on screen are the server's.
  const coversRoot = !!treeRoot && (scope.path.path ?? []).some((p) => p.broker_id === treeRoot);
  const rootId = guard.kind === "allow" || !coversRoot || !treeRoot ? scope.nodeId : treeRoot;
  const rootName = (scope.path.path ?? []).find((p) => p.broker_id === rootId)?.display_name ?? rootId;
  const firstPage = { offset: 0, limit: PLAN_PAGE_LIMIT };
  const probe = { queryKey: keys.feePlans(rootId, firstPage), queryFn: () => fetchFeePlans(rootId, firstPage) };
  const tab = search.tab ?? DEFAULT_TAB;

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("feePlans.title")} subtitle={t("feePlans.subtitle")} coverage="subtree" />
      {tab === "share-plans" || tab === "share-assignments" ? <ShareRulesCard /> : <FeeRulesCard />}
      <Tabs
        label={t("feePlans.tabs")}
        idBase={idBase}
        tabs={PLAN_TABS.map((k) => ({ key: k, label: t(TAB_LABEL[k]) }))}
        value={tab}
        onChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next === DEFAULT_TAB ? undefined : next, plan: undefined }) })
        }
        className="mb-4"
      />
      <TabPanel idBase={idBase} value={tab}>
        {tab === "fee-plans" && (
          <FeePlansPanel
            rootId={rootId}
            planId={search.plan}
            onPlan={(plan) => void navigate({ search: (prev) => ({ ...prev, plan }) })}
          />
        )}
        {tab === "fee-assignments" && <FeeAssignmentsPanel rootId={rootId} rootName={rootName} />}
        {tab === "share-plans" && (
          <SharePlansPanel
            rootId={rootId}
            rootName={rootName}
            planId={search.plan}
            onPlan={(plan) => void navigate({ search: (prev) => ({ ...prev, plan }) })}
          />
        )}
        {tab === "share-assignments" && <ShareAssignmentsPanel rootId={rootId} rootName={rootName} />}
      </TabPanel>
    </Guarded>
  );
}

/** A descendant is selected: plan management lives on the root. */
function SwitchToRoot({ root }: { root: StaffScopeItem }) {
  const { t } = useI18n();
  const select = useSelectNode();
  const name = root.broker.display_name;
  return (
    <section aria-labelledby="fee-root-title" className="space-y-3">
      <h1 id="fee-root-title" className="page-title">
        {t("feePlans.rootOnlyTitle")}
      </h1>
      <p className="page-subtitle">{t("feePlans.rootOnlyBody", { root: name })}</p>
      <Button onClick={() => select(root.broker.broker_id)}>{t("feePlans.switchToRoot", { root: name })}</Button>
    </section>
  );
}
